import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Typography,
    Box,
    Alert,
    Avatar,
    Stack,
    Chip,
    useTheme,
    Divider,
    AlertTitle,
} from "@mui/material";
import {
    MdSchool,
    MdSave,
    MdCancel,
    MdPersonOff,
    MdSwapHoriz,
    MdCheckCircleOutline,
    MdNavigateNext, // New: For next/forward indicator
    MdWarning, // New: For warning alerts
} from "react-icons/md";
import { fetchCourses, updateUserCourse } from "../../api/adminApi.jsx";

// Utility function to get user initials for the Avatar
const getInitials = (name) => {
    if (!name) return "??";
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
};

// Constant for representing the un-enrolled state
const UNENROLLED_VALUE = "UNENROLLED_STUDENT";

const ChangeCourseDialog = ({ open, onClose, user, onUpdateSuccess }) => {
    const theme = useTheme();

    // Destructure user, providing fallbacks for safety
    const { id, name = "N/A", course_name: currentCourse = null, email } = user || {};

    const [courses, setCourses] = useState([]);

    // Use useMemo to calculate initialSelection only when currentCourse changes
    const initialSelection = useMemo(() => currentCourse || UNENROLLED_VALUE, [currentCourse]);

    const [selectedCourse, setSelectedCourse] = useState(initialSelection);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [warning, setWarning] = useState(null); // New: State for pre-save warnings

    // Load unique courses when dialog opens or user changes
    const loadAvailableCourses = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetchCourses();
            const uniqueCourses = res.data || [];

            setCourses(uniqueCourses);

            // Re-set selection based on loaded data (for re-opening the dialog)
            const newSelection = currentCourse && uniqueCourses.includes(currentCourse)
                ? currentCourse
                : UNENROLLED_VALUE;

            setSelectedCourse(newSelection);
        } catch (err) {
            console.error("Failed to load courses:", err);
            setError("Could not load the list of available courses from the server. Please check your network and try again.");
            setCourses([]);
        } finally {
            setLoading(false);
        }
    }, [currentCourse]);

    useEffect(() => {
        if (open) {
            loadAvailableCourses();
        }
    }, [open, loadAvailableCourses]);

    // Reset local state when dialog closes
    useEffect(() => {
        if (!open) {
            setError(null);
            setWarning(null); // Clear warnings on close
            // Reset selection to the initial value for the next open
            setSelectedCourse(initialSelection);
        } else {
            // Reset selection when opening
            setSelectedCourse(initialSelection);
        }
    }, [open, initialSelection]);


    const handleSave = async () => {
        let courseToSave = selectedCourse;
        let newCourseNameForSuccess = selectedCourse;
        setWarning(null); // Clear any existing warnings

        // 1. If the selection is the un-enrolled constant, set the API value to null
        if (selectedCourse === UNENROLLED_VALUE) {
            courseToSave = null;
            newCourseNameForSuccess = null;
        }

        // 2. Final check: prevent API call if the selection is the same as the current enrollment state
        if (courseToSave === currentCourse) {
            onClose();
            return;
        }

        // 3. Optional: Add a confirmation/warning step for un-enrollment
        if (isUnenrolling) {
            if (!window.confirm(`WARNING: Are you sure you want to un-enroll ${name} from their current course? This action may require manual re-enrollment.`)) {
                return; // User cancelled the confirmation
            }
        }

        setSaving(true);
        setError(null);
        try {
            await updateUserCourse(id, courseToSave);

            // Call success callback and pass the new course name (null or string)
            onUpdateSuccess(newCourseNameForSuccess);

        } catch (err) {
            console.error("Update failed:", err);
            const errorMessage = err.response?.data?.message || err.message || "Failed to update course due to a network or server error.";
            setError(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    // Derived states for UI logic
    const isSaveDisabled = saving || loading || selectedCourse === initialSelection;
    const isUnenrolling = selectedCourse === UNENROLLED_VALUE && currentCourse !== null;
    const isEnrolling = selectedCourse !== UNENROLLED_VALUE && currentCourse === null;
    const isSwitching = selectedCourse !== UNENROLLED_VALUE && currentCourse !== null && selectedCourse !== currentCourse;

    const actionText = isUnenrolling ? "Un-enroll" : isEnrolling ? "Enroll" : isSwitching ? "Switch" : "No Change";

    // Set a dynamic pre-save warning based on selection
    useEffect(() => {
        if (isUnenrolling) {
            setWarning(`Action: The user will be **removed** from ${currentCourse}.`);
        } else if (isSwitching) {
            setWarning(`Action: The user's course will be **changed** from ${currentCourse} to ${selectedCourse}.`);
        } else if (isEnrolling) {
            setWarning(`Action: The user will be **enrolled** in ${selectedCourse}.`);
        } else {
            setWarning(null);
        }
    }, [selectedCourse, currentCourse, isUnenrolling, isSwitching, isEnrolling]);

    // console.log("Courses from API:", courses);


    // Determine the save button's color based on the action
    const saveButtonColor = isUnenrolling ? "error" : "primary";

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            // Elevated, rounded paper for a modern look
            PaperProps={{ sx: { borderRadius: 2, padding: 0, boxShadow: theme.shadows[10] } }}
        >
            <DialogTitle sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                fontWeight: 600, // Slightly lighter font weight
                fontSize: '1.25rem', // Larger title font
                color: theme.palette.primary.main, // Use primary color for main title
                backgroundColor: theme.palette.primary.light + '10', // Very light background tint
                py: 2,
                px: 3,
            }}>
                <MdSwapHoriz size={28} color={theme.palette.primary.main} />
                Modify Course Enrollment
            </DialogTitle>

            <DialogContent dividers sx={{ px: 3, py: 3 }}>

                {/* User Card - Enhanced Presentation */}
                <Stack
                    direction="row"
                    spacing={3}
                    alignItems="center"
                    sx={{
                        mb: 4,
                        p: 2.5,
                        bgcolor: theme.palette.grey[50], // Lighter background for the card
                        borderRadius: 1.5,
                        border: `1px solid ${theme.palette.grey[200]}`
                    }}
                >
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 60, height: 60, fontSize: '1.75rem' }}>
                        {getInitials(name)}
                    </Avatar>
                    <Box>
                        <Typography variant="h5" component="div" fontWeight={600} color="text.primary">{name}</Typography>
                        <Typography variant="body2" color="text.secondary">ID: **{id}** | {email || "No Email Provided"}</Typography>
                    </Box>
                </Stack>

                {/* Current vs. New Enrollment - Visualized with an arrow */}
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>

                    {/* Current Course */}
                    <Box flex={1} textAlign="center">
                        <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', mb: 1 }}>CURRENT</Typography>
                        {currentCourse ? (
                            <Chip
                                label={currentCourse}
                                size="large"
                                color="primary"
                                variant="outlined"
                                icon={<MdSchool />}
                                sx={{ fontWeight: 600, px: 1, borderColor: theme.palette.primary.light }}
                            />
                        ) : (
                            <Chip
                                label="Unenrolled"
                                size="large"
                                color="error"
                                variant="outlined"
                                icon={<MdPersonOff />}
                                sx={{ fontStyle: 'italic', px: 1 }}
                            />
                        )}
                    </Box>

                    {/* Arrow Indicator */}
                    <MdNavigateNext size={40} color={theme.palette.primary.light} />

                    {/* New Selection */}
                    <Box flex={1} textAlign="center">
                        <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: 'block', mb: 1 }}>NEW SELECTION</Typography>
                        {selectedCourse === UNENROLLED_VALUE ? (
                            <Chip
                                label="Un-enroll"
                                size="large"
                                color="error"
                                variant="filled"
                                icon={<MdPersonOff />}
                                sx={{ fontWeight: 600, px: 1, bgcolor: theme.palette.error.light }}
                            />
                        ) : (
                            <Chip
                                label={selectedCourse === currentCourse ? "Current Course" : selectedCourse}
                                size="large"
                                color={selectedCourse === currentCourse ? "success" : "secondary"}
                                variant="filled"
                                icon={selectedCourse === currentCourse ? <MdCheckCircleOutline /> : <MdSchool />}
                                sx={{ fontWeight: 600, px: 1 }}
                            />
                        )}
                    </Box>
                </Stack>

                <Divider sx={{ mb: 3 }} />

                {/* Error & Warning Alerts */}
                {error &&
                    <Alert severity="error" sx={{ mb: 2 }}>
                        <AlertTitle>Update Error</AlertTitle>
                        {error}
                    </Alert>
                }
                {warning && !isSaveDisabled &&
                    <Alert severity={isUnenrolling ? "warning" : "info"} icon={<MdWarning />} sx={{ mb: 2 }}>
                        {/* Render HTML content within Typography */}
                        <Typography variant="body2" component="div">
                            <AlertTitle>{isUnenrolling ? "Un-enrollment Warning" : "Pending Action"}</AlertTitle>
                            <span dangerouslySetInnerHTML={{ __html: warning.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                        </Typography>
                    </Alert>
                }

                {/* Course Selection Dropdown */}
                {loading ? (
                    <Box display="flex" flexDirection="column" alignItems="center" p={3} sx={{ height: 120, justifyContent: 'center' }}>
                        <CircularProgress size={30} color="primary" />
                        <Typography variant="body1" sx={{ mt: 2, color: theme.palette.primary.main }}>Loading Available Courses...</Typography>
                    </Box>
                ) : (
                    <FormControl fullWidth variant="outlined" disabled={courses.length === 0 || saving}>
                        <InputLabel id="course-select-label">Select New Course</InputLabel>
                        <Select
                            labelId="course-select-label"
                            value={selectedCourse}
                            onChange={(e) => setSelectedCourse(e.target.value)}
                            label="Select New Course"
                            sx={{ '& .MuiSelect-select': { fontWeight: 500 } }}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                        overflowY: "auto",
                                    },
                                },
                            }}
                        >

                            {/* UN-ENROLL OPTION */}
                            <MenuItem
                                value={UNENROLLED_VALUE}
                                sx={{ fontStyle: "italic", color: theme.palette.error.main, fontWeight: 600, py: 1.5 }}
                            >
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <MdPersonOff />
                                    <Typography>Un-enroll Student (Set to None)</Typography>
                                </Stack>
                            </MenuItem>

                            <Divider />

                            {/* ALL COURSES */}
                            {courses.map((course, index) => (
                                <MenuItem
                                    key={index}
                                    value={course}
                                    sx={{
                                        fontWeight: course === currentCourse ? 700 : 400,
                                        bgcolor: course === currentCourse ? theme.palette.success.light + "20" : "inherit",
                                    }}
                                >
                                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                                        {course}
                                        {course === currentCourse && (
                                            <Chip
                                                label="Current"
                                                size="small"
                                                color="success"
                                                variant="filled"
                                                icon={<MdCheckCircleOutline />}
                                            />
                                        )}
                                    </Box>
                                </MenuItem>
                            ))}

                        </Select>

                    </FormControl>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, pt: 2, borderTop: `1px solid ${theme.palette.grey[200]}` }}>
                <Button
                    onClick={onClose}
                    color="inherit"
                    disabled={saving}
                    startIcon={<MdCancel />}
                    sx={{ textTransform: 'none' }} // Professional look
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color={saveButtonColor} // Dynamic color for high-impact actions
                    disabled={isSaveDisabled}
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <MdSave />}
                    sx={{ minWidth: 150, textTransform: 'none' }} // Ensures button width is consistent
                >
                    {saving ? "Updating..." : `${actionText} Course`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChangeCourseDialog;